/**
 * TAR file extraction utilities for MuJoCo filesystem.
 * Extracts TAR archives into the Emscripten virtual filesystem.
 */

import type { MainModule, EmscriptenFS } from './mujocoTypes';
import { mkdirRecursive } from './filesystemUtils';

/**
 * Clears the entire TAR cache.
 * Call this to force fresh downloads of all TAR files.
 *
 * @returns Promise resolving to true if cache was deleted, false otherwise
 */
export async function clearTarCache(): Promise<boolean> {
  try {
    const cacheName = 'mujoco-tar-cache';
    const deleted = await caches.delete(cacheName);
    if (deleted) {
      console.log(`Cleared entire tar cache (${cacheName})`);
    } else {
      console.log(`Cache ${cacheName} was not found`);
    }
    return deleted;
  } catch (error) {
    console.error(`Error clearing tar cache:`, error);
    return false;
  }
}

/**
 * Deletes a specific file from the cache.
 *
 * @param tarPath - Path to the TAR file to remove from cache
 * @returns Promise resolving to true if file was deleted, false otherwise
 */
export async function deleteFileFromCache(tarPath: string): Promise<boolean> {
  try {
    const cacheName = 'mujoco-tar-cache';
    const cache = await caches.open(cacheName);
    const deleted = await cache.delete(tarPath);
    if (deleted) {
      console.log(`Deleted ${tarPath} from cache`);
    } else {
      console.log(`File ${tarPath} was not found in cache`);
    }
    return deleted;
  } catch (error) {
    console.error(`Error deleting ${tarPath} from cache:`, error);
    return false;
  }
}

/**
 * Extracts a TAR file into the MuJoCo filesystem.
 *
 * @param fs - The Emscripten filesystem module
 * @param tarPath - URL path to the TAR file
 * @param basePath - Base directory in the virtual filesystem (default: '/working')
 * @param addToCache - Whether to cache the downloaded TAR file (default: true)
 * @param clearCache - Whether to clear the cache before downloading (default: false)
 */
export async function extractTarToFilesystem(
  fs: MainModule['FS'],
  tarPath: string,
  basePath: string = '/working',
  addToCache: boolean = true,
  clearCache: boolean = false
): Promise<void> {
  // Determine if tarPath is a full URL or a relative path
  let fetchUrl: string;
  if (tarPath.startsWith('http://') || tarPath.startsWith('https://')) {
    // Direct URL (e.g., R2 bucket URL)
    fetchUrl = tarPath;
  } else if (tarPath.startsWith('/api/scenes/tar/')) {
    // Already an API endpoint path, use as-is
    fetchUrl = tarPath;
  } else {
    // Legacy format: convert relative path to API endpoint (e.g., "/scenes/ithor-bundled.tar" -> "/api/scenes/tar/ithor-bundled")
    // Remove .tar extension if present, as the API endpoint expects just the name
    const tarName = tarPath.replace(/\.tar$/, '').replace(/^\/scenes\//, '');
    fetchUrl = `/api/scenes/tar/${tarName}`;
  }

  // Clear cache if requested
  if (clearCache) {
    await clearTarCache();
  }

  // Use Cache API to avoid re-downloading TAR files
  const cacheName = 'mujoco-tar-cache';
  const cache = await caches.open(cacheName);

  // Try to get from cache first
  let response = await cache.match(fetchUrl);

  if (!response) {
    // Not in cache, fetch from network
    console.log(`Fetching ${fetchUrl} from network...`);
    response = await fetch(fetchUrl);

    if (!response.ok) {
      throw new Error(
        `Failed to load tar file: ${tarPath} (${response.status} ${response.statusText})`
      );
    }

    // Clone the response because it can only be consumed once
    // Store the clone in cache for future use (if addToCache is true)
    if (addToCache) {
      const responseToCache = response.clone();
      await cache.put(fetchUrl, responseToCache);
      console.log(`Cached ${fetchUrl} for future use`);
    }
  } else {
    console.log(`Using cached version of ${fetchUrl}`);
  }

  const arrayBuffer = await response.arrayBuffer();
  console.log(`Loaded tar file: ${tarPath}, size: ${arrayBuffer.byteLength} bytes`);

  const jsUntarModule = await import('js-untar');
  const untar = jsUntarModule.default as any;

  if (typeof untar !== 'function') {
    throw new Error(
      'untar is not a function. Module structure: ' + JSON.stringify(Object.keys(jsUntarModule))
    );
  }

  const files = await untar(arrayBuffer);

  console.log(`Extracted ${files.length} files from tar`);

  let filesWritten = 0;
  let directoriesSkipped = 0;
  let emptyFilesSkipped = 0;

  const typedFS = fs as unknown as EmscriptenFS;

  // Write files to mujoco filesystem
  for (const file of files) {
    // Handle different path formats
    let cleanPath = file.name;

    // Remove leading ./ or /
    cleanPath = cleanPath.replace(/^\.\//, '').replace(/^\//, '');

    // Skip if path is empty or just "." or ".."
    if (!cleanPath || cleanPath === '.' || cleanPath === '..') {
      directoriesSkipped++;
      continue;
    }

    if (cleanPath.endsWith('/')) {
      directoriesSkipped++;
      mkdirRecursive(fs, cleanPath.slice(0, -1), basePath);
      continue;
    }

    const lastSlash = cleanPath.lastIndexOf('/');
    if (lastSlash >= 0) {
      const dirPath = cleanPath.substring(0, lastSlash);
      mkdirRecursive(fs, dirPath, basePath);
    }

    if (file.buffer && file.buffer.byteLength > 0) {
      const fileData =
        file.buffer instanceof ArrayBuffer ? new Uint8Array(file.buffer) : file.buffer;

      const fullPath = `${basePath}/${cleanPath}`;
      try {
        typedFS.writeFile(fullPath, fileData);
        filesWritten++;
      } catch (e) {
        console.warn(`Failed to write file ${fullPath}:`, e);
      }
    } else {
      emptyFilesSkipped++;
    }
  }

  console.log(
    `Extraction summary: ${filesWritten} files written, ${directoriesSkipped} directories skipped, ${emptyFilesSkipped} empty entries skipped`
  );
}

/**
 * Discovers the XML file in a directory.
 * All scenes now have robots, so we always prefer robot versions with 'no_ceiling' if available.
 *
 * @param fs - The Emscripten filesystem module
 * @param searchDir - Directory to search (default: '/working')
 * @returns The relative path to the XML file, or null if not found
 */
export function discoverXmlFile(
  fs: MainModule['FS'],
  searchDir: string = '/working'
): string | null {
  const typedFS = fs as unknown as EmscriptenFS;

  const isDirectory = (stat: any): boolean => {
    return (stat.mode & 0o040000) !== 0;
  };

  const xmlFiles: string[] = [];

  const collectXmlFiles = (dir: string): void => {
    try {
      const entries = typedFS.readdir(dir);
      for (const entry of entries) {
        if (entry === '.' || entry === '..') {
          continue;
        }

        const fullPath = dir === '/' ? `/${entry}` : `${dir}/${entry}`;
        try {
          const stat = typedFS.stat(fullPath);
          if (isDirectory(stat)) {
            collectXmlFiles(fullPath);
          } else if (entry.endsWith('.xml')) {
            const relativePath = fullPath.startsWith(searchDir + '/')
              ? fullPath.substring((searchDir + '/').length)
              : entry;
            xmlFiles.push(relativePath);
          }
        } catch {
          continue;
        }
      }
    } catch {
      // Silently ignore errors
    }
  };

  collectXmlFiles(searchDir);

  if (xmlFiles.length === 0) {
    return null;
  }

  // All scenes have robots now - prefer robot XMLs with 'no_ceiling' if available
  const robotXmls = xmlFiles.filter((f) => f.includes('_with_robot') || f.includes('with_robot'));

  if (robotXmls.length > 0) {
    // If multiple robot XMLs exist, prefer the one with "no_ceiling" in the name
    const noCeilingXml = robotXmls.find((f) => f.includes('no_ceiling'));
    const selectedXml = noCeilingXml ?? robotXmls[0];

    console.log(
      `🤖 Found robot XML: ${searchDir}/${selectedXml} (from ${xmlFiles.length} total XML files)`
    );
    return selectedXml ?? null;
  }

  // Fallback to first XML found (shouldn't happen for our scenes)
  const firstXml = xmlFiles[0];
  console.log(
    `⚠️ No robot XML found, using: ${searchDir}/${firstXml} (from ${xmlFiles.length} total XML files)`
  );
  return firstXml ?? null;
}

// Expose cache management functions globally for easy access from browser console
declare global {
  interface Window {
    deleteFileFromCache: (tarPath: string) => Promise<boolean>;
    clearTarCache: () => Promise<boolean>;
  }
}

if (typeof window !== 'undefined') {
  window.deleteFileFromCache = deleteFileFromCache;
  window.clearTarCache = clearTarCache;
}
